import { type ByteArray, formatEther, parseEther, type Hex } from 'viem';
import {
  composePromptFromState,
  type HandlerCallback,
  type AgentRuntime,
  type Memory,
  type State,
} from '@elizaos/core';

import { initWalletProvider, type WalletProvider } from '../providers/wallet';
import type { Transaction, TransferParams } from '../types';
import { transferTemplate } from '../templates';

export { transferTemplate };

// Exported for tests
export class TransferAction {
  constructor(private walletProvider: WalletProvider) {}

  async transfer(params: TransferParams): Promise<Transaction> {
    const walletClient = this.walletProvider.getWalletClient(params.fromChain);

    console.log(
      `Transferring: ${params.amount} tokens from (${walletClient.account.address} to (${params.toAddress} on ${params.fromChain})`
    );

    if (!params.data) {
      params.data = '0x';
    }

    try {
      const hash = await walletClient.sendTransaction({
        account: walletClient.account,
        to: params.toAddress,
        value: parseEther(params.amount),
        data: params.data as Hex,
        kzg: {
          blobToKzgCommitment: (_: ByteArray): ByteArray => {
            throw new Error('Function not implemented.');
          },
          computeBlobKzgProof: (_blob: ByteArray, _commitment: ByteArray): ByteArray => {
            throw new Error('Function not implemented.');
          },
        },
        chain: undefined,
      });

      return {
        hash,
        from: walletClient.account.address,
        to: params.toAddress,
        value: parseEther(params.amount),
        data: params.data as Hex,
      };
    } catch (error) {
      throw new Error(`Transfer failed: ${error.message}`);
    }
  }
}

const buildTransferDetails = async (
  state: State,
  runtime: AgentRuntime,
  wp: WalletProvider
): Promise<TransferParams> => {
  const context = composePromptFromState({
    state,
    template: transferTemplate,
  });

  const chains = Object.keys(wp.chains);

  const contextWithChains = context.replace(
    'SUPPORTED_CHAINS',
    chains.map((item) => `"${item}"`).join('|')
  );

  const transferDetails = (await runtime.useModel('objectGeneration', {
    context: contextWithChains,
  })) as TransferParams;

  const existingChain = wp.chains[transferDetails.fromChain];

  if (!existingChain) {
    throw new Error(
      `The chain ${transferDetails.fromChain} not configured yet. Add the chain or choose one from configured: ${chains.toString()}`
    );
  }

  return transferDetails;
};

export const transferAction = {
  name: 'transfer',
  description: 'Transfer tokens between addresses on the same chain',
  handler: async (
    runtime: AgentRuntime,
    _message: Memory,
    state: State,
    _options: Record<string, unknown>,
    callback?: HandlerCallback
  ) => {
    console.log('Transfer action handler called');

    try {
      const walletProvider = initWalletProvider(runtime);
      const action = new TransferAction(walletProvider);

      // Compose transfer context
      const paramOptions = await buildTransferDetails(state, runtime, walletProvider);

      const transferResp = await action.transfer(paramOptions);
      if (callback) {
        callback({
          text: `Successfully transferred ${paramOptions.amount} tokens to ${paramOptions.toAddress}\nTransaction Hash: ${transferResp.hash}`,
          content: {
            success: true,
            hash: transferResp.hash,
            amount: formatEther(transferResp.value),
            recipient: transferResp.to,
            chain: paramOptions.fromChain,
          },
        });
      }
      return true;
    } catch (error) {
      console.error('Error during token transfer:', error);
      if (callback) {
        callback({
          text: `Error transferring tokens: ${error.message}`,
          content: { error: error.message },
        });
      }
      return false;
    }
  },
  template: transferTemplate,
  validate: async (runtime: AgentRuntime) => {
    const privateKey = runtime.getSetting('ARTHERA_PRIVATE_KEY');
    return typeof privateKey === 'string' && privateKey.startsWith('0x');
  },
  examples: [
    [
      {
        user: 'assistant',
        content: {
          text: "I'll help you transfer 1 AA to 0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
          action: 'SEND_TOKENS',
        },
      },
      {
        user: 'user',
        content: {
          text: 'Transfer 1 AA to 0x742d35Cc6634C0532925a3b844Bc454e4438f44e',
          action: 'SEND_TOKENS',
        },
      },
    ],
  ],
  similes: ['SEND_TOKENS', 'TOKEN_TRANSFER', 'MOVE_TOKENS'],
};
